/**
 * Étape 5 — Autorisations et vie privée.
 *
 * Les trois consentements au même endroit : les soins, l'image, les données.
 * Ils étaient dispersés — l'accord pour les soins au milieu du formulaire
 * médical, une case photo perdue sous l'adresse, et rien du tout sur les
 * données. Un parent ne pouvait pas répondre à la question « qu'est-ce que
 * j'ai accepté ? », parce qu'aucun écran ne la posait entière.
 *
 * ## Le droit à l'image en cinq questions
 *
 * Aucune n'est pré-remplie et aucune n'est facultative. « Non » est une réponse
 * parfaitement acceptable, et c'est même tout l'intérêt : une base où le refus
 * est explicite vaut mieux qu'une base où il se déduit d'un silence.
 *
 * Les réseaux sociaux ne posent qu'une question. La politique (§7) n'y autorise
 * que des images où l'enfant est de dos ou non reconnaissable : ce n'est pas un
 * choix offert à la famille, c'est une règle qu'on lui annonce — et l'intitulé
 * de la question D la porte.
 */

import { Link } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import { DESCRIPTIONS, type Reponse } from '../../../lib/droitImage';
import {
  CHEMIN_POLITIQUE,
  COURRIEL_VIE_PRIVEE,
  MAJ_POLITIQUE,
  VERSION_POLITIQUE,
} from '../../../lib/confidentialite';
import { Bloc, Case, Info, Navigation, OuiNon } from './champs';
import { ACCORD_SOINS } from './textes';
import type { Autorisations, Erreurs } from './fiche';

const EtapeAutorisations = ({
  valeur,
  onChange,
  erreurs,
  onPrecedent,
  onSuivant,
  prenom,
}: {
  valeur: Autorisations;
  onChange: (v: Autorisations) => void;
  erreurs: Erreurs;
  onPrecedent: () => void;
  onSuivant: () => void;
  prenom: string;
}) => {
  const enfant = prenom.trim() || 'votre enfant';

  const majImage = (champs: Partial<Autorisations['image']>) =>
    onChange({ ...valeur, image: { ...valeur.image, ...champs } });

  const repondre = (cle: keyof Autorisations['image'], reponse: Reponse) =>
    majImage({ [cle]: reponse } as Partial<Autorisations['image']>);

  return (
    <div className="space-y-8">
      {/* ---------------------------------------------------------------- */}
      <Bloc titre="En cas de problème de santé">
        <p className="whitespace-pre-line text-sm leading-relaxed text-gray-700">{ACCORD_SOINS}</p>
        <Case
          coche={valeur.soins}
          onChange={(v) => onChange({ ...valeur, soins: v })}
          label="Oui, je marque mon accord."
          requis
          erreur={erreurs.soins}
        />
      </Bloc>

      {/* ---------------------------------------------------------------- */}
      <div className="border-t border-gray-200 pt-8">
        <Bloc
          titre="Photos et vidéos"
          requis
          intro={
            <>
              Pendant les activités, nous photographions et filmons les enfants&nbsp;: c’est la
              trace de ce qu’ils ont vécu, et ce que nous montrons aux familles. Vous décidez,
              usage par usage, de ce que nous pouvons en faire. Chaque réponse est indépendante,
              et vous pourrez en changer à tout moment depuis la fiche de {enfant}.
            </>
          }
        >
          <Info>
            <p className="mb-1.5 flex items-center font-semibold text-gray-900">
              <ShieldCheck className="mr-1.5 h-4 w-4 text-primary-600" />
              Ce que nous ne faisons jamais
            </p>
            <ul className="space-y-1">
              <li>· associer le nom de votre enfant à une image publiée&nbsp;;</li>
              <li>· vendre ou céder des photos à un tiers&nbsp;;</li>
              <li>· indiquer où l’image a été prise.</li>
            </ul>
            <p className="mt-2">
              Un enfant capable de comprendre la situation peut lui-même demander qu’une photo de
              lui ne soit plus utilisée. Un retrait est immédiat pour l’avenir&nbsp;; il ne peut
              rien changer à ce qui est déjà imprimé.
            </p>
          </Info>

          <div className="space-y-3">
            {DESCRIPTIONS.map((d) => (
              <div
                key={d.cle}
                className="rounded-xl border border-gray-200 p-4 sm:flex sm:items-start sm:gap-6"
              >
                <div className="flex-1">
                  <p className="font-medium text-gray-900">
                    <span className="mr-2 inline-flex h-5 w-5 items-center justify-center rounded bg-gray-100 text-xs font-semibold text-gray-600">
                      {d.lettre}
                    </span>
                    {d.titre}
                  </p>
                  <p className="mt-1 text-sm leading-relaxed text-gray-500">{d.exemple}</p>
                </div>
                <div className="mt-3 sm:mt-0">
                  <OuiNon
                    valeur={valeur.image[d.cle]}
                    onChange={(v) => repondre(d.cle, v)}
                    erreur={erreurs[d.cle]}
                  />
                </div>
              </div>
            ))}
          </div>

          <Case
            coche={valeur.image.accordAutreParent}
            onChange={(v) => majImage({ accordAutreParent: v })}
            label="Je confirme disposer de l’accord de l’autre parent ou du second titulaire de l’autorité parentale."
            aide="Demandé uniquement lorsqu’une publication hors de l’espace des familles est autorisée."
            erreur={erreurs.accordAutreParent}
          />
        </Bloc>
      </div>

      {/* ---------------------------------------------------------------- */}
      <div className="border-t border-gray-200 pt-8">
        <Bloc
          titre="Vos données"
          intro="Ce que nous collectons, pourquoi, avec qui nous le partageons et combien de temps nous le conservons."
        >
          <div className="rounded-xl border border-gray-200 p-4 text-sm leading-relaxed text-gray-700">
            <p>
              Les données de {enfant} servent à l’inscrire, l’accueillir en sécurité, gérer les
              présences et la facturation. Les données de santé sont réservées aux équipes et,
              le cas échéant, aux prestataires de santé consultés. Nous ne vendons pas les
              données, ne faisons pas de profilage, et n’analysons pas les dossiers des enfants
              par intelligence artificielle.
            </p>
            <p className="mt-2">
              Vous pouvez à tout moment consulter, corriger ou faire supprimer ces données, et
              retirer un consentement, en écrivant à{' '}
              <a
                href={`mailto:${COURRIEL_VIE_PRIVEE}`}
                className="font-medium text-primary-700 underline underline-offset-2"
              >
                {COURRIEL_VIE_PRIVEE}
              </a>
              .
            </p>
            <p className="mt-3">
              <Link
                to={CHEMIN_POLITIQUE}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-primary-700 underline underline-offset-2"
              >
                Lire la politique de confidentialité
              </Link>{' '}
              <span className="text-gray-500">
                (version {VERSION_POLITIQUE}, {MAJ_POLITIQUE}) — elle s’ouvre dans un nouvel
                onglet, votre fiche reste ici.
              </span>
            </p>
          </div>

          <Case
            coche={valeur.politiqueLue}
            onChange={(v) => onChange({ ...valeur, politiqueLue: v })}
            label={`J’ai pris connaissance de la politique de confidentialité d’Éclosion ASBL (version ${VERSION_POLITIQUE}).`}
            requis
            erreur={erreurs.politiqueLue}
          />
        </Bloc>
      </div>

      <Navigation onPrecedent={onPrecedent} onSuivant={onSuivant} libelleSuivant="Voir le récapitulatif" />
    </div>
  );
};

export default EtapeAutorisations;
